import React, { useState, useEffect, useContext } from "react";
import {
  onSnapshot,
  query,
  updateDoc,
  addDoc,
} from "firebase/firestore";
import { propertyDoc, propertyCollection } from "../utils/firestorePaths";
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  CardActions,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  IconButton,
  Divider,
  Avatar,
  Stack,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  CheckCircle as CheckIcon,
  Schedule as PendingIcon,
  Assignment as WorkOrderIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Print as PrintIcon,
  KeyboardDoubleArrowUp as HighIcon,
  DragHandle as MediumIcon,
  KeyboardDoubleArrowDown as LowIcon,
} from "@mui/icons-material";
import PropertyContext from "../contexts/PropertyContext";

const FrontDeskWorkOrders = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [rooms, setRooms] = useState([]);
  const [openWorkOrders, setOpenWorkOrders] = useState([]);
  const [pendingApproval, setPendingApproval] = useState([]);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);

  // New work order form state
  const [selectedRoom, setSelectedRoom] = useState("");
  const [pteApproved, setPteApproved] = useState(false);
  const [description, setDescription] = useState("");
  const [importance, setImportance] = useState("medium");

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("room"); // "room" or "description"
  const [searchResults, setSearchResults] = useState([]);

  // View work order modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);

  const { selectedProperty, selectedClient } = useContext(PropertyContext);

  // Get status value based on completed key
  const getStatusValue = (order) => {
    if (!order.completed) {
      return "open";
    } else if (order.completed && order.status === "canceled") {
      return "canceled";
    } else {
      return "completed";
    }
  };

  // Get importance icon and color
  const getImportanceIcon = (importance) => {
    const level = importance || "medium";

    switch (level) {
      case "high":
        return { Icon: HighIcon, color: "error" };
      case "low":
        return { Icon: LowIcon, color: "success" };
      case "medium":
      default:
        return { Icon: MediumIcon, color: "warning" };
    }
  };

  // Open work order details
  const handleOpenWorkOrder = (order) => {
    console.log("Work Order Details:", order);
    setSelectedWorkOrder(order);
    setViewModalOpen(true);
  };

  // Update work order status
  const handleUpdateStatus = async (orderId, newStatus) => {
    const orderRef = propertyDoc(
      selectedClient,
      selectedProperty,
      "workOrders",
      orderId
    );

    let updateData = {};

    if (newStatus === "open") {
      // Open status: completed = false
      updateData = {
        completed: false,
        status: "open",
      };
    } else if (newStatus === "canceled") {
      // Canceled status: completed = true, status = "canceled"
      updateData = {
        completed: true,
        status: "canceled",
      };
    } else if (newStatus === "completed") {
      // Completed status: completed = true, status != "canceled"
      updateData = {
        completed: true,
        status: "completed",
      };
    }

    await updateDoc(orderRef, updateData);

    // Update local state
    setSelectedWorkOrder((prev) => ({
      ...prev,
      ...updateData,
    }));
  };

  // Print work order
  const handlePrintWorkOrder = (order) => {
    const printWindow = window.open("", "_blank");

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Work Order - Room ${order.room}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 40px;
              max-width: 800px;
              margin: 0 auto;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #333;
              padding-bottom: 20px;
            }
            .header h1 {
              margin: 0;
              font-size: 28px;
            }
            .header p {
              margin: 5px 0;
              color: #666;
            }
            .section {
              margin-bottom: 25px;
            }
            .section-title {
              font-weight: bold;
              font-size: 14px;
              color: #666;
              margin-bottom: 8px;
              text-transform: uppercase;
            }
            .section-content {
              font-size: 16px;
              padding: 10px;
              background: #f5f5f5;
              border-radius: 4px;
            }
            .info-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 10px;
            }
            .info-label {
              font-weight: bold;
              color: #666;
            }
            .badge {
              display: inline-block;
              padding: 4px 12px;
              border-radius: 12px;
              font-size: 12px;
              font-weight: bold;
              margin-right: 8px;
            }
            .badge-success {
              background: #4caf50;
              color: white;
            }
            .badge-error {
              background: #f44336;
              color: white;
            }
            .badge-warning {
              background: #ff9800;
              color: white;
            }
            .badge-primary {
              background: #2196f3;
              color: white;
            }
            @media print {
              body {
                padding: 20px;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Work Order</h1>
            <p>Room ${order.room}</p>
          </div>

          <div class="section">
            <div class="section-title">Status</div>
            <div class="section-content">
              <span class="badge ${
                order.completed
                  ? "badge-success"
                  : order.markedCompleted
                  ? "badge-warning"
                  : "badge-primary"
              }">
                ${
                  order.completed
                    ? "Completed"
                    : order.markedCompleted
                    ? "Pending Approval"
                    : "Open"
                }
              </span>
              <span class="badge ${order.pteApproved ? "badge-success" : "badge-error"}">
                ${order.pteApproved ? "PTE Approved" : "No PTE"}
              </span>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Task Description</div>
            <div class="section-content">
              ${order.orderDetails}
            </div>
          </div>

          <div class="section">
            <div class="section-title">Timeline</div>
            <div class="section-content">
              <div class="info-row">
                <span class="info-label">Reported:</span>
                <span>${formatTime(order.reportedTime)}</span>
              </div>
              ${
                order.markedCompletedTime
                  ? `<div class="info-row">
                      <span class="info-label">Marked Completed:</span>
                      <span>${formatTime(order.markedCompletedTime)}</span>
                    </div>`
                  : ""
              }
            </div>
          </div>

          ${
            order.markedCompleteBy
              ? `<div class="section">
                  <div class="section-title">Completed By</div>
                  <div class="section-content">
                    ${getEmployeeName(order.markedCompleteBy)}
                  </div>
                </div>`
              : ""
          }

          ${
            order.phoneNumber
              ? `<div class="section">
                  <div class="section-title">Contact Information</div>
                  <div class="section-content">
                    ${order.phoneNumber}
                    ${order.receiveUpdates ? " (Receive Updates)" : ""}
                  </div>
                </div>`
              : ""
          }

          <div class="section">
            <div class="section-title">Additional Details</div>
            <div class="section-content">
              <div class="info-row">
                <span class="info-label">Status:</span>
                <span>${order.status || "N/A"}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Signed For:</span>
                <span>${order.isSignedFor ? "Yes" : "No"}</span>
              </div>
            </div>
          </div>

          <script>
            window.onload = function() {
              window.print();
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  // Search handler
  const handleSearch = () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const allOrders = [...openWorkOrders, ...pendingApproval, ...completedOrders];

    let results = [];
    if (searchType === "room") {
      results = allOrders.filter((order) =>
        order.room?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    } else {
      results = allOrders.filter((order) =>
        order.orderDetails?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setSearchResults(results);
  };

  useEffect(() => {
    if (!selectedProperty) return;

    setIsLoading(true);
    const roomsRef = propertyCollection(selectedClient, selectedProperty, "units");
    const workOrdersRef = propertyCollection(selectedClient, selectedProperty, "workOrders");
    const employeesRef = propertyCollection(selectedClient, selectedProperty, "Staff");

    const unsubRooms = onSnapshot(query(roomsRef), (snapshot) => {
      const roomsArray = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setRooms(roomsArray.sort((a, b) =>
        a.displayName.localeCompare(b.displayName, undefined, { numeric: true })
      ));
    });

    const unsubWorkOrders = onSnapshot(query(workOrdersRef), (snapshot) => {
      const allOrders = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Categorize work orders
      const open = allOrders.filter(
        (order) => !order.completed && !order.markedCompleted
      );
      const pending = allOrders.filter(
        (order) => !order.completed && order.markedCompleted
      );
      const completed = allOrders.filter((order) => order.completed);

      setOpenWorkOrders(open);
      setPendingApproval(pending);
      setCompletedOrders(completed.slice(0, 10)); // Last 10 completed
    });

    const unsubEmployees = onSnapshot(query(employeesRef), (snapshot) => {
      const employeeList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setEmployees(employeeList);
      setIsLoading(false);
    });

    return () => {
      unsubRooms();
      unsubWorkOrders();
      unsubEmployees();
    };
  }, [selectedProperty]);

  const handleCreateWorkOrder = async () => {
    if (!selectedRoom || !description) {
      alert("Please select a room and provide a description");
      return;
    }

    const workOrderData = {
      completed: false,
      isSignedFor: false,
      markedCompleteBy: null,
      markedCompleted: false,
      markedCompletedTime: null,
      room: selectedRoom,
      pteApproved: pteApproved,
      orderDetails: description,
      status: "pending",
      phoneNumber: null,
      receiveUpdates: false,
      reportedTime: new Date(),
      importance: importance,
    };

    const workOrdersRef = propertyCollection(
      selectedClient,
      selectedProperty,
      "workOrders"
    );

    await addDoc(workOrdersRef, workOrderData);

    // Reset form
    setSelectedRoom("");
    setPteApproved(false);
    setDescription("");
    setImportance("medium");
    setModalOpen(false);
  };

  const handleApproveOrder = async (orderId, employeeId) => {
    const orderRef = propertyDoc(
      selectedClient,
      selectedProperty,
      "workOrders",
      orderId
    );

    await updateDoc(orderRef, {
      completed: true,
    });

    // Update employee work order count
    if (employeeId) {
      const employeeRef = propertyDoc(
        selectedClient,
        selectedProperty,
        "Staff",
        employeeId
      );
      const employee = employees.find((emp) => emp.id === employeeId);
      const currentCount = Number(employee?.workOrders || 0);

      await updateDoc(employeeRef, {
        workOrders: currentCount + 1,
      });
    }
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find((emp) => emp.id === employeeId);
    return employee?.name || "Unknown";
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "N/A";
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString();
    } catch {
      return "N/A";
    }
  };

  // Work Order Card Component
  const WorkOrderCard = ({ order, showApprove = false, showCompleted = false }) => {
    const { Icon: ImportanceIcon, color: importanceColor } = getImportanceIcon(order.importance);

    return (
      <Card
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          transition: "all 0.3s ease",
          cursor: "pointer",
          "&:hover": {
            boxShadow: 6,
            transform: "translateY(-2px)",
          },
        }}
        onClick={() => handleOpenWorkOrder(order)}
      >
        <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
          <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <ImportanceIcon color={importanceColor} />
              <Typography variant="h6" fontWeight="600">
                Room {order.room}
              </Typography>
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
              {order.pteApproved ? (
                <Chip label="PTE" color="success" size="small" />
              ) : (
                <Chip label="No PTE" color="error" size="small" />
              )}
              {showCompleted && (
                <Chip
                  label="Completed"
                  color="success"
                  size="small"
                  icon={<CheckIcon />}
                />
              )}
            </Box>
          </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 2,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            flexGrow: 1,
          }}
        >
          {order.orderDetails}
        </Typography>

        {/* Bottom section with divider and timestamps */}
        <Box mt="auto">
          <Divider sx={{ mb: 1.5 }} />

          <Stack spacing={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="caption" color="text.secondary">
                Reported:
              </Typography>
              <Typography variant="caption">
                {formatTime(order.reportedTime)}
              </Typography>
            </Box>

            {order.markedCompleteBy && (
              <Box display="flex" alignItems="center" gap={1}>
                <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                  {getEmployeeName(order.markedCompleteBy).charAt(0)}
                </Avatar>
                <Typography variant="caption">
                  {getEmployeeName(order.markedCompleteBy)}
                </Typography>
              </Box>
            )}

            {order.markedCompletedTime && (
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="caption" color="text.secondary">
                  Completed:
                </Typography>
                <Typography variant="caption">
                  {formatTime(order.markedCompletedTime)}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      </CardContent>

      {showApprove && (
        <CardActions>
          <Button
            fullWidth
            variant="contained"
            color="success"
            startIcon={<CheckIcon />}
            onClick={(e) => {
              e.stopPropagation();
              handleApproveOrder(order.id, order.markedCompleteBy);
            }}
          >
            Approve
          </Button>
        </CardActions>
      )}
    </Card>
    );
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: "calc(100vh - 100px)", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" fontWeight="600" gutterBottom>
            Work Orders
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage and track maintenance requests
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          onClick={() => setModalOpen(true)}
        >
          New Work Order
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={4}>
          <Card
            sx={{
              bgcolor: "primary.main",
              color: "white",
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: 6,
              }
            }}
            onClick={() => setCurrentTab(0)}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h3" fontWeight="700">
                    {openWorkOrders.length}
                  </Typography>
                  <Typography variant="body2">Open Orders</Typography>
                </Box>
                <WorkOrderIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card
            sx={{
              bgcolor: "warning.main",
              color: "white",
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: 6,
              }
            }}
            onClick={() => setCurrentTab(1)}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h3" fontWeight="700">
                    {pendingApproval.length}
                  </Typography>
                  <Typography variant="body2">Pending Approval</Typography>
                </Box>
                <PendingIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card
            sx={{
              bgcolor: "success.main",
              color: "white",
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: 6,
              }
            }}
            onClick={() => setCurrentTab(2)}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h3" fontWeight="700">
                    {completedOrders.length}
                  </Typography>
                  <Typography variant="body2">Completed - Pending Guest Approval</Typography>
                </Box>
                <CheckIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={currentTab} onChange={(e, val) => setCurrentTab(val)}>
          <Tab label={`Open (${openWorkOrders.length})`} />
          <Tab label={`Pending Approval (${pendingApproval.length})`} />
          <Tab label={`Completed (${completedOrders.length})`} />
          <Tab label="Search" icon={<SearchIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ flexGrow: 1, overflow: "auto" }}>
        {currentTab === 0 && (
          <Grid container spacing={2}>
            {openWorkOrders.length === 0 ? (
              <Grid item xs={12}>
                <Box textAlign="center" py={8}>
                  <WorkOrderIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No open work orders
                  </Typography>
                </Box>
              </Grid>
            ) : (
              openWorkOrders.map((order) => (
                <Grid item xs={12} sm={6} md={4} key={order.id}>
                  <WorkOrderCard order={order} />
                </Grid>
              ))
            )}
          </Grid>
        )}

        {currentTab === 1 && (
          <Grid container spacing={2}>
            {pendingApproval.length === 0 ? (
              <Grid item xs={12}>
                <Box textAlign="center" py={8}>
                  <PendingIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No orders pending approval
                  </Typography>
                </Box>
              </Grid>
            ) : (
              pendingApproval.map((order) => (
                <Grid item xs={12} sm={6} md={4} key={order.id}>
                  <WorkOrderCard order={order} showApprove />
                </Grid>
              ))
            )}
          </Grid>
        )}

        {currentTab === 2 && (
          <Grid container spacing={2}>
            {completedOrders.length === 0 ? (
              <Grid item xs={12}>
                <Box textAlign="center" py={8}>
                  <CheckIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No completed orders
                  </Typography>
                </Box>
              </Grid>
            ) : (
              completedOrders.map((order) => (
                <Grid item xs={12} sm={6} md={4} key={order.id}>
                  <WorkOrderCard order={order} showCompleted />
                </Grid>
              ))
            )}
          </Grid>
        )}

        {currentTab === 3 && (
          <Box>
            {/* Search Controls */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Stack spacing={3}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Search By</FormLabel>
                  <RadioGroup
                    row
                    value={searchType}
                    onChange={(e) => setSearchType(e.target.value)}
                  >
                    <FormControlLabel
                      value="room"
                      control={<Radio />}
                      label="Room Number"
                    />
                    <FormControlLabel
                      value="description"
                      control={<Radio />}
                      label="Task Description"
                    />
                  </RadioGroup>
                </FormControl>

                <Box display="flex" gap={2}>
                  <TextField
                    fullWidth
                    label={searchType === "room" ? "Enter room number" : "Enter description keywords"}
                    placeholder={searchType === "room" ? "e.g., 101" : "e.g., leaky faucet"}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleSearch();
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                    sx={{ minWidth: 120 }}
                  >
                    Search
                  </Button>
                </Box>
              </Stack>
            </Paper>

            {/* Search Results */}
            {searchResults.length > 0 ? (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="600">
                          Room
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="600">
                          Status
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="600">
                          Open Date
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="600">
                          Task Description
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {searchResults.map((order) => (
                      <TableRow
                        key={order.id}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleOpenWorkOrder(order)}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="600">
                            {order.room}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              order.completed
                                ? "Completed"
                                : order.markedCompleted
                                ? "Pending Approval"
                                : "Open"
                            }
                            color={
                              order.completed
                                ? "success"
                                : order.markedCompleted
                                ? "warning"
                                : "primary"
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatTime(order.reportedTime)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 400,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {order.orderDetails}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : searchQuery.trim() !== "" ? (
              <Box textAlign="center" py={8}>
                <SearchIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No results found for "{searchQuery}"
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Try adjusting your search terms or search type
                </Typography>
              </Box>
            ) : (
              <Box textAlign="center" py={8}>
                <SearchIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Enter a search query to find work orders
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Search by room number or task description
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* New Work Order Dialog */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight="600">
              New Work Order
            </Typography>
            <IconButton onClick={() => setModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} mt={1}>
            <FormControl fullWidth>
              <InputLabel>Room Number</InputLabel>
              <Select
                value={selectedRoom}
                onChange={(e) => setSelectedRoom(e.target.value)}
                label="Room Number"
              >
                {rooms.map((room) => (
                  <MenuItem key={room.id} value={room.displayName}>
                    {room.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>PTE Approved</InputLabel>
              <Select
                value={pteApproved}
                onChange={(e) => setPteApproved(e.target.value)}
                label="PTE Approved"
              >
                <MenuItem value={false}>No</MenuItem>
                <MenuItem value={true}>Yes</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Importance</InputLabel>
              <Select
                value={importance}
                onChange={(e) => setImportance(e.target.value)}
                label="Importance"
              >
                <MenuItem value="low">
                  <Box display="flex" alignItems="center" gap={1}>
                    <LowIcon color="success" />
                    Low
                  </Box>
                </MenuItem>
                <MenuItem value="medium">
                  <Box display="flex" alignItems="center" gap={1}>
                    <MediumIcon color="warning" />
                    Medium
                  </Box>
                </MenuItem>
                <MenuItem value="high">
                  <Box display="flex" alignItems="center" gap={1}>
                    <HighIcon color="error" />
                    High
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Description"
              multiline
              rows={4}
              fullWidth
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the issue or maintenance needed..."
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateWorkOrder}
            disabled={!selectedRoom || !description}
          >
            Create Work Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Work Order Details Modal */}
      <Dialog
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight="600">
              Work Order Details - Room {selectedWorkOrder?.room}
            </Typography>
            <IconButton onClick={() => setViewModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedWorkOrder && (
            <Stack spacing={3} mt={1}>
              {/* Status Row */}
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={getStatusValue(selectedWorkOrder)}
                    onChange={(e) => handleUpdateStatus(selectedWorkOrder.id, e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="open">Open</MenuItem>
                    <MenuItem value="canceled">Canceled</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                  </Select>
                </FormControl>

                <Box display="flex" gap={1} alignItems="center">
                  {(() => {
                    const { Icon: ImportanceIcon, color: importanceColor } = getImportanceIcon(selectedWorkOrder.importance);
                    return (
                      <Chip
                        icon={<ImportanceIcon />}
                        label={`${(selectedWorkOrder.importance || "medium").charAt(0).toUpperCase() + (selectedWorkOrder.importance || "medium").slice(1)} Priority`}
                        color={importanceColor}
                        size="small"
                      />
                    );
                  })()}
                  {selectedWorkOrder.pteApproved ? (
                    <Chip label="PTE Approved" color="success" size="small" />
                  ) : (
                    <Chip label="No PTE" color="error" size="small" />
                  )}
                </Box>
              </Box>

              <Divider />

              {/* Room Information */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Room Number
                </Typography>
                <Typography variant="h6">{selectedWorkOrder.room}</Typography>
              </Box>

              {/* Task Description */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Task Description
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: "background.default" }}>
                  <Typography variant="body1">{selectedWorkOrder.orderDetails}</Typography>
                </Paper>
              </Box>

              {/* Timeline Information */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Timeline
                </Typography>
                <Stack spacing={1.5}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">Reported:</Typography>
                    <Typography variant="body2" fontWeight="600">
                      {formatTime(selectedWorkOrder.reportedTime)}
                    </Typography>
                  </Box>
                  {selectedWorkOrder.markedCompletedTime && (
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">Marked Completed:</Typography>
                      <Typography variant="body2" fontWeight="600">
                        {formatTime(selectedWorkOrder.markedCompletedTime)}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>

              {/* Employee Information */}
              {selectedWorkOrder.markedCompleteBy && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Completed By
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ width: 40, height: 40 }}>
                      {getEmployeeName(selectedWorkOrder.markedCompleteBy).charAt(0)}
                    </Avatar>
                    <Typography variant="body1" fontWeight="600">
                      {getEmployeeName(selectedWorkOrder.markedCompleteBy)}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Contact Information */}
              {selectedWorkOrder.phoneNumber && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Contact Information
                  </Typography>
                  <Typography variant="body1">{selectedWorkOrder.phoneNumber}</Typography>
                  {selectedWorkOrder.receiveUpdates && (
                    <Chip label="Receive Updates" size="small" sx={{ mt: 1 }} />
                  )}
                </Box>
              )}

              {/* Additional Details */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Additional Details
                </Typography>
                <Stack spacing={1}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Signed For:</Typography>
                    <Typography variant="body2" fontWeight="600">
                      {selectedWorkOrder.isSignedFor ? "Yes" : "No"}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, justifyContent: "space-between" }}>
          <Box>
            <Button onClick={() => setViewModalOpen(false)} variant="outlined">
              Close
            </Button>
            {selectedWorkOrder && !selectedWorkOrder.completed && selectedWorkOrder.markedCompleted && (
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckIcon />}
                onClick={() => {
                  handleApproveOrder(selectedWorkOrder.id, selectedWorkOrder.markedCompleteBy);
                  setViewModalOpen(false);
                }}
                sx={{ ml: 1 }}
              >
                Approve Work Order
              </Button>
            )}
          </Box>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={() => handlePrintWorkOrder(selectedWorkOrder)}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FrontDeskWorkOrders;
